import { Bodies, Body, Vector } from 'matter-js';
import { BUBBLE_SCALE_FACTOR, COLLISION_CATEGORIES } from '../utils/constants';
import { AbstractEntity } from './AbstractEntity';

export interface BubbleConfig<DatumType = any> {
    x: number;
    y: number;
    radius: number;
    id?: string;
    datum?: DatumType
    content?: HTMLElement; // HTML string o ID de template para el contenido
    onSelectToggle?: (bubble: HTMLElement, isSelected: boolean) => void;
    auraColor?: string;
    /** @default true */
    isSelectable?: boolean;
}

const DEFAULT_PROPERTIES: Matter.IBodyDefinition = {
    // --- FÍSICAS TIPO BILLAR ---

    // 1. Rebote muerto (0.0 a 0.2)
    // 0.1 permite un micro rebote para que no se sienta rígido como piedra,
    // pero absorbe casi toda la energía del choque.
    restitution: 0.13,

    // 2. Alta fricción con el aire (0.05 a 0.1)
    // Valor normal es 0.01. Al subirlo a 0.05, la burbuja se detiene rápido
    // después de soltarla. Da control.
    frictionAir: 0.08,

    // 3. Fricción estática/cinética (0.1 a 0.5)
    // Ayuda a que dejen de rodar y se queden quietas antes.
    friction: 0.1,

    // 4. Densidad (Peso)
    // Un poco más alto que el default (0.001) para dar autoridad al movimiento.
    density: 0.002,
    // inertia: Infinity,   // Opcional: Evita que la burbuja rote sobre su eje si no lo quieres
};


export class BubbleEntity<DatumType = any> extends AbstractEntity<DatumType> {
    public _radius: number;
    public _isSelected: boolean = false;
    public _isInCluster: boolean = false; // Flag para saber si está atrapada en un cluster
    private auraColor: string = null;
    private content: HTMLElement;
    private onSelectToggle?: (bubble: HTMLElement, isSelected: boolean) => void;

    // FLAGS
    private isSelectable: boolean = true; // Si false, no se puede seleccionar (ej: burbujas dentro de cluster)

    constructor(config: BubbleConfig<DatumType>) {
        // 1. Crear el Elemento DOM
        const element = document.createElement('div');
        element.classList.add('bubble-node');

        // Accesibilidad: la burbuja se comporta como un botón de alternancia (seleccionada/no seleccionada)
        element.setAttribute('role', 'button');
        element.setAttribute('aria-pressed', 'false');

        // Asignamos tamaño visual explícito para coincidir con la física
        const size = config.radius * 2;
        element.style.width = `${size}px`;
        element.style.height = `${size}px`;

        // Insertar contenido interno (puede ser un componente Lit más tarde)
        if (config.content) {
            element.appendChild(config.content);
        }

        // 2. Crear el Cuerpo Físico (Matter.js)
        const body = Bodies.circle(config.x, config.y, config.radius, {
            label: 'BUBBLE',
            ...DEFAULT_PROPERTIES,
            collisionFilter: {
                category: COLLISION_CATEGORIES.BUBBLE,
                // mask: COLLISION_CATEGORIES.DEFAULT | COLLISION_CATEGORIES.BUBBLE | COLLISION_CATEGORIES.WALL | COLLISION_CATEGORIES.CLUSTER
            }
        });

        super(element, body, config.datum);
        this._radius = config.radius;
        this.onSelectToggle = config.onSelectToggle;
        this.content = config.content;
        this._AuraColor = config.auraColor;
        this.isSelectable = config.isSelectable !== false;
        this.updateAccessibilityAttributes();

        // Si pasaron un ID, lo usamos, si no, Matter genera uno
        if (config.id) {
            // @ts-ignore
            this._id = config.id;
            // Nota: body.id es numérico en Matter, usamos body.label o un mapa externo para strings
        }
    }

    // *************************************************************************
    //#region UTILS

    /**
     * Refleja el estado actual de selectabilidad/atrapamiento en atributos DOM
     * (tabIndex, aria-disabled) para que los usuarios de teclado y lectores de
     * pantalla puedan saber si la burbuja es interactuable.
     */
    private updateAccessibilityAttributes() {
        const isInteractable = this.isSelectable && !this._isInCluster;
        this._element.tabIndex = isInteractable ? 0 : -1;
        this._element.setAttribute('aria-disabled', String(!isInteractable));
    }

    // #endregion
    // **************************************************************************

    // *************************************************************************
    //#region API

    get _AuraColor(): string {
        return this.auraColor;
    }

    set _AuraColor(color: string) {
        this.auraColor = color;
        if (color) {
            this._element.style.setProperty('--aura-color', color);
        } else {
            this._element.style.removeProperty('--aura-color');
        }
    }

    get _ContentElement(): HTMLElement {
        return this.content;
    }

    get _IsSelectable(): boolean {
        return this.isSelectable;
    }

    public _SetIsSelectable(isSelectable: boolean, forceUnselect: boolean = true) {
        this.isSelectable = isSelectable;
        this.updateAccessibilityAttributes();
        if (!isSelectable && forceUnselect) {
            this._ToggleSelection(false);
        }
    }

    /** Reemplaza el callback invocado al alternar la selección (útil al refrescar la configuración de la burbuja) */
    public _SetOnSelectToggle(callback?: (bubble: HTMLElement, isSelected: boolean) => void) {
        this.onSelectToggle = callback;
    }

    /**
     * Actualiza el contenido visual de la burbuja.
     * Útil para cambiar lo que muestra sin recrear la burbuja.
     */
    public _UpdateContent(newContent: HTMLElement) {
        if (this.content) {
            this._element.replaceChild(newContent, this.content);
        } else {
            this._element.appendChild(newContent);
        }
        this.content = newContent;
    }

    /** Método para "atrapar" la burbuja */
    public _SetTrappedState(isTrapped: boolean) {
        this._isInCluster = isTrapped;
        this.isSelectable = !isTrapped; // Si está atrapada, no es seleccionable individualmente
        this.updateAccessibilityAttributes();

        if (isTrapped) {
            // this._ToggleSelection(false); // Quitar halo de selección

            // ENCOGER FÍSICAMENTE
            Body.scale(this._body, BUBBLE_SCALE_FACTOR, BUBBLE_SCALE_FACTOR);
            this._radius *= BUBBLE_SCALE_FACTOR;

            // ENCOGER VISUALMENTE CON ANIMACIÓN POR CÓDIGO
            this._SetTargetScale(BUBBLE_SCALE_FACTOR, 0.2);
            this._element.classList.add('trapped');

            // QUITAR COLISIONES CON CUALQUIER COSA
            // this.body.collisionFilter.mask = 0;

            this._body.collisionFilter.category = COLLISION_CATEGORIES.INNER_BUBBLE;

            // Truco: group -1 hace que no choquen entre ellas
            // Matter prioriza 'group'. Si dos cuerpos tienen el mismo group negativo, NO chocan.
            this._body.collisionFilter.group = -1;

            // Aumenta la fricción aire drásticamente
            this._body.frictionAir = 0.5; // Como moverse en miel :)
        } else {
            // RESTAURAR TAMAÑO FÍSICO
            const inverseScale = 1 / BUBBLE_SCALE_FACTOR;
            Body.scale(this._body, inverseScale, inverseScale);
            this._radius /= BUBBLE_SCALE_FACTOR;

            // RESTAURAR TAMAÑO VISUAL CON ANIMACIÓN POR CÓDIGO
            this._SetTargetScale(1, 0.2);
            this._element.classList.remove('trapped');

            // RESTAURAR COLISIONES
            // this.body.collisionFilter.mask = 0xFFFFFFFF;

            this._body.collisionFilter.category = COLLISION_CATEGORIES.BUBBLE; // Vuelven a ser "normales"
            this._body.collisionFilter.group = 0;

            this._body.frictionAir = DEFAULT_PROPERTIES.frictionAir;
        }
    }

    /** Cambia el estado de selección */
    public _ToggleSelection(forceState?: boolean): void {
        this._isSelected = (forceState !== undefined ? forceState : !this._isSelected) && this.isSelectable;;
        this._element.setAttribute('aria-pressed', String(this._isSelected));

        if (this._isSelected) {
            this._element.classList.add('selected');
            // const selectionIndicator = this._element.querySelector('.selected-element-mask');
            // if (!selectionIndicator) {
            //     const temp = document.createElement('div');
            //     temp.innerHTML = mask_round_border_points;
            //     const svgElement = temp.firstElementChild;
            //     svgElement.classList.add('selected-element-mask');
            //     this._element.appendChild(svgElement);
            // }
        } else {
            this._element.classList.remove('selected');
            // const selectionIndicator = this._element.querySelector('.selected-element-mask');
            // if (selectionIndicator) {
            //     selectionIndicator.remove();
            // }
        }

        if (this.onSelectToggle) {
            this.onSelectToggle(this.content, this._isSelected);
        }
    }

    /** Método específico para "empujar" la burbuja (usado por el input manager) */
    public _ApplyImpulse(vector: Vector): void {
        Body.applyForce(this._body, this._body.position, vector);
    }

    public override _LaunchTo(x: number, y: number, speed: number = 0.15, collisionsEnabled: boolean = true) {
        if (this._isInCluster) return;
        super._LaunchTo(x, y, speed, collisionsEnabled);
    }

    // public override _Sync(): void {
    //     super._Sync();
    //     // Opcional: Si la velocidad es alta, deformar ligeramente la burbuja (squash & stretch)
    // }

    //#endregion
    // *************************************************************************
}